import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MonacoEditor from '@monaco-editor/react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Switch,
    FormControlLabel,
    Tooltip,
    LinearProgress
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { updateFunction } from 'store/slices/lambdaFunctions';

// icons
import {
    IconCode,
    IconDeviceFloppy,
    IconArrowLeft,
    IconSettings,
    IconTestPipe,
    IconRocket,
    IconHistory,
    IconAlertCircle,
    IconCheck,
    IconCopy,
    IconTrash,
    IconPlus,
    IconInfoCircle,
    IconLockOpen,
    IconVariable,
    IconBrandPython,
    IconBrandJavascript
} from '@tabler/icons-react';

// Sample function code templates
const codeTemplates = {
    python: `import json

def lambda_handler(event, context):
    """
    Lambda function handler
    
    Args:
        event: The event payload
        context: Runtime information
        
    Returns:
        dict: Response object
    """
    try:
        # Your code here
        result = process_request(event)
        
        return {
            'statusCode': 200,
            'body': json.dumps(result)
        }
    except Exception as e:
        return {
            'statusCode': 500,
            'body': json.dumps({'error': str(e)})
        }

def process_request(event):
    # Implement your logic here
    return {'message': 'Success'}`,

    javascript: `exports.handler = async (event, context) => {
    try {
        // Your code here
        const result = await processRequest(event);
        
        return {
            statusCode: 200,
            body: JSON.stringify(result)
        };
    } catch (error) {
        return {
            statusCode: 500,
            body: JSON.stringify({ error: error.message })
        };
    }
};

async function processRequest(event) {
    // Implement your logic here
    return { message: 'Success' };
}`
};

// ==============================|| FUNCTION EDITOR ||============================== //

const FunctionEditor = () => {
    const theme = useTheme();
    const { functionId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { functions } = useSelector((state) => state.lambdaFunctions);

    const [func, setFunc] = useState(null);
    const [code, setCode] = useState('');
    const [language, setLanguage] = useState('python');
    const [tabValue, setTabValue] = useState(0);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [deploying, setDeploying] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);
    const [envVars, setEnvVars] = useState([{ key: '', value: '' }]);
    const [dependencies, setDependencies] = useState('');

    // Configuration state
    const [config, setConfig] = useState({
        memory: '256Mi',
        timeout: 30,
        rate_limit: '1000/hour',
        cache_ttl: 300,
        retry_count: 3,
        async_enabled: false,
        logging_level: 'INFO'
    });

    useEffect(() => {
        const foundFunc = functions.find((f) => f.id === functionId);
        if (foundFunc) {
            setFunc(foundFunc);
            setConfig(foundFunc.config);
            // Load code from storage or use template
            const savedCode = localStorage.getItem(`function_code_${functionId}`);
            setCode(savedCode || codeTemplates[language]);

            // Load environment variables
            const savedEnvVars = localStorage.getItem(`function_env_${functionId}`);
            if (savedEnvVars) {
                setEnvVars(JSON.parse(savedEnvVars));
            }

            // Load dependencies
            const savedDeps = localStorage.getItem(`function_deps_${functionId}`);
            setDependencies(savedDeps || '');
        }
    }, [functionId, functions, language]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSave = async () => {
        setSaving(true);

        // Save to localStorage (simulating backend save)
        localStorage.setItem(`function_code_${functionId}`, code);
        localStorage.setItem(`function_env_${functionId}`, JSON.stringify(envVars));
        localStorage.setItem(`function_deps_${functionId}`, dependencies);

        // Update function config in Redux
        dispatch(
            updateFunction({
                id: functionId,
                config,
                updated_at: new Date().toISOString()
            })
        );

        setTimeout(() => {
            setSaving(false);
            setSnackbar({
                open: true,
                message: 'Function saved successfully',
                severity: 'success'
            });
        }, 1000);
    };

    const handleDeploy = () => {
        setDeploying(true);

        setTimeout(() => {
            dispatch(
                updateFunction({
                    id: functionId,
                    status: 'deployed',
                    version: incrementVersion(func.version),
                    updated_at: new Date().toISOString()
                })
            );

            setDeploying(false);
            setSnackbar({
                open: true,
                message: 'Function deployed successfully',
                severity: 'success'
            });
        }, 2000);
    };

    const handleTest = () => {
        setTesting(true);
        setTimeout(() => {
            setTesting(false);
            navigate(`/lambda/test/${functionId}`);
        }, 1000);
    };

    const incrementVersion = (version) => {
        const parts = version.split('.');
        parts[2] = (parseInt(parts[2]) + 1).toString();
        return parts.join('.');
    };

    const handleAddEnvVar = () => {
        setEnvVars([...envVars, { key: '', value: '' }]);
    };

    const handleRemoveEnvVar = (index) => {
        setEnvVars(envVars.filter((_, i) => i !== index));
    };

    const handleEnvVarChange = (index, field, value) => {
        const newEnvVars = [...envVars];
        newEnvVars[index][field] = value;
        setEnvVars(newEnvVars);
    };

    const handleConfigChange = (field, value) => {
        setConfig({ ...config, [field]: value });
    };

    if (!func) {
        return (
            <MainCard>
                <Alert severity="error">Function not found</Alert>
            </MainCard>
        );
    }

    return (
        <>
            <MainCard
                title={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                            <IconButton onClick={() => navigate('/lambda/dashboard')}>
                                <IconArrowLeft />
                            </IconButton>
                            <Typography variant="h3">Edit Function: {func.name}</Typography>
                            <Chip label={`v${func.version}`} size="small" />
                            <Chip label={func.status} size="small" color={func.status === 'deployed' ? 'success' : 'warning'} />
                        </Box>
                        <Box display="flex" gap={1}>
                            <Button variant="outlined" startIcon={<IconTestPipe />} onClick={handleTest} disabled={testing}>
                                Test
                            </Button>
                            <Button variant="outlined" startIcon={<IconDeviceFloppy />} onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<IconRocket />}
                                onClick={handleDeploy}
                                disabled={deploying || func.status === 'deploying'}
                            >
                                {deploying ? 'Deploying...' : 'Deploy'}
                            </Button>
                        </Box>
                    </Box>
                }
            >
                {(saving || deploying) && <LinearProgress sx={{ mb: 2 }} />}

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Code Editor" icon={<IconCode />} iconPosition="start" />
                        <Tab label="Configuration" icon={<IconSettings />} iconPosition="start" />
                        <Tab label="Environment Variables" icon={<IconVariable />} iconPosition="start" />
                        <Tab label="Version History" icon={<IconHistory />} iconPosition="start" />
                    </Tabs>
                </Box>

                {/* Code Editor Tab */}
                {tabValue === 0 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <InputLabel>Language</InputLabel>
                                            <Select value={language} label="Language" onChange={(e) => setLanguage(e.target.value)}>
                                                <MenuItem value="python">
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <IconBrandPython size={16} />
                                                        Python
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem value="javascript">
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <IconBrandJavascript size={16} />
                                                        JavaScript
                                                    </Box>
                                                </MenuItem>
                                            </Select>
                                        </FormControl>

                                        <Box display="flex" gap={1}>
                                            <Tooltip title="Format Code">
                                                <IconButton size="small">
                                                    <IconCode />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Copy Code">
                                                <IconButton size="small" onClick={() => navigator.clipboard.writeText(code)}>
                                                    <IconCopy />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                        <MonacoEditor
                                            height="600px"
                                            language={language}
                                            theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'vs-light'}
                                            value={code}
                                            onChange={setCode}
                                            options={{
                                                minimap: { enabled: true },
                                                fontSize: 14,
                                                wordWrap: 'on',
                                                automaticLayout: true,
                                                scrollBeyondLastLine: false
                                            }}
                                        />
                                    </Paper>

                                    <Box mt={2}>
                                        <Alert severity="info" icon={<IconInfoCircle />}>
                                            <Typography variant="caption">
                                                Dependencies: {dependencies || 'No dependencies specified'}
                                            </Typography>
                                        </Alert>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Configuration Tab */}
                {tabValue === 1 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Resource Configuration
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Memory</InputLabel>
                                                <Select
                                                    value={config.memory}
                                                    label="Memory"
                                                    onChange={(e) => handleConfigChange('memory', e.target.value)}
                                                >
                                                    <MenuItem value="128Mi">128 MB</MenuItem>
                                                    <MenuItem value="256Mi">256 MB</MenuItem>
                                                    <MenuItem value="512Mi">512 MB</MenuItem>
                                                    <MenuItem value="1Gi">1 GB</MenuItem>
                                                    <MenuItem value="2Gi">2 GB</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Timeout (seconds)"
                                                type="number"
                                                value={config.timeout}
                                                onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
                                                inputProps={{ min: 1, max: 900 }}
                                                helperText="Maximum execution time in seconds (1-900)"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Rate Limit"
                                                value={config.rate_limit}
                                                onChange={(e) => handleConfigChange('rate_limit', e.target.value)}
                                                helperText="e.g., 1000/hour, 100/minute"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Cache TTL (seconds)"
                                                type="number"
                                                value={config.cache_ttl}
                                                onChange={(e) => handleConfigChange('cache_ttl', parseInt(e.target.value))}
                                                helperText="Response cache time-to-live"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Advanced Settings
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Retry Count"
                                                type="number"
                                                value={config.retry_count}
                                                onChange={(e) => handleConfigChange('retry_count', parseInt(e.target.value))}
                                                inputProps={{ min: 0, max: 5 }}
                                                helperText="Number of automatic retries on failure"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Logging Level</InputLabel>
                                                <Select
                                                    value={config.logging_level}
                                                    label="Logging Level"
                                                    onChange={(e) => handleConfigChange('logging_level', e.target.value)}
                                                >
                                                    <MenuItem value="DEBUG">DEBUG</MenuItem>
                                                    <MenuItem value="INFO">INFO</MenuItem>
                                                    <MenuItem value="WARNING">WARNING</MenuItem>
                                                    <MenuItem value="ERROR">ERROR</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={config.async_enabled}
                                                        onChange={(e) => handleConfigChange('async_enabled', e.target.checked)}
                                                    />
                                                }
                                                label="Enable Asynchronous Execution"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Alert severity="warning" icon={<IconAlertCircle />}>
                                                <Typography variant="caption">
                                                    Changes to configuration will take effect on next deployment
                                                </Typography>
                                            </Alert>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Dependencies
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={dependencies}
                                        onChange={(e) => setDependencies(e.target.value)}
                                        placeholder={
                                            language === 'python'
                                                ? 'requests==2.28.1\nnumpy==1.23.0\npandas==1.5.0'
                                                : 'express@4.18.2\naxios@1.3.0\nlodash@4.17.21'
                                        }
                                        helperText={`Enter ${language === 'python' ? 'pip' : 'npm'} dependencies, one per line`}
                                        sx={{ fontFamily: 'monospace' }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Environment Variables Tab */}
                {tabValue === 2 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                        <Typography variant="h5">Environment Variables</Typography>
                                        <Button variant="outlined" startIcon={<IconPlus />} onClick={handleAddEnvVar} size="small">
                                            Add Variable
                                        </Button>
                                    </Box>

                                    <List>
                                        {envVars.map((envVar, index) => (
                                            <ListItem key={index} sx={{ px: 0 }}>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={5}>
                                                        <TextField
                                                            fullWidth
                                                            label="Key"
                                                            value={envVar.key}
                                                            onChange={(e) => handleEnvVarChange(index, 'key', e.target.value)}
                                                            placeholder="VARIABLE_NAME"
                                                            sx={{ '& input': { fontFamily: 'monospace' } }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Value"
                                                            value={envVar.value}
                                                            onChange={(e) => handleEnvVarChange(index, 'value', e.target.value)}
                                                            placeholder="value"
                                                            type="password"
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <IconButton size="small">
                                                                        <IconLockOpen size={16} />
                                                                    </IconButton>
                                                                )
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <IconButton color="error" onClick={() => handleRemoveEnvVar(index)}>
                                                            <IconTrash size={18} />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        ))}
                                    </List>

                                    {envVars.length === 0 && (
                                        <Box textAlign="center" py={4}>
                                            <Typography variant="body2" color="textSecondary">
                                                No environment variables configured
                                            </Typography>
                                        </Box>
                                    )}

                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <Typography variant="caption">
                                            Environment variables are encrypted and securely stored. They will be available to your function
                                            during execution.
                                        </Typography>
                                    </Alert>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Version History Tab */}
                {tabValue === 3 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Version History
                                    </Typography>

                                    <List>
                                        {[
                                            { version: func.version, date: func.updated_at, status: 'current', author: func.created_by },
                                            { version: '1.0.1', date: '2024-07-16T10:00:00Z', status: 'deployed', author: func.created_by },
                                            { version: '1.0.0', date: '2024-07-15T08:00:00Z', status: 'archived', author: func.created_by }
                                        ].map((version, index) => (
                                            <React.Fragment key={version.version}>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        {version.status === 'current' ? (
                                                            <IconCheck color={theme.palette.success.main} />
                                                        ) : (
                                                            <IconHistory />
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Typography variant="subtitle1">Version {version.version}</Typography>
                                                                {version.status === 'current' && (
                                                                    <Chip label="Current" size="small" color="success" />
                                                                )}
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box>
                                                                <Typography variant="caption" display="block">
                                                                    Deployed on {new Date(version.date).toLocaleString()}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    by {version.author}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                    <Box display="flex" gap={1}>
                                                        {version.status !== 'current' && (
                                                            <Button size="small" variant="outlined">
                                                                Rollback
                                                            </Button>
                                                        )}
                                                        <Button size="small" variant="text">
                                                            View Diff
                                                        </Button>
                                                    </Box>
                                                </ListItem>
                                                {index < 2 && <Divider />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </MainCard>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default FunctionEditor;
